import { useMemo } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  Alert,
  Card,
  CHART_NEGATIVE,
  CHART_POSITIVE,
  DataTable,
  EmptyState,
  GroupedBarChart,
  PageHeader,
  Sparkline,
  Spinner,
  StackedBarChart,
  StatTile,
} from "@empite/ui-components";
import type { Column } from "@empite/ui-components";
import type {
  Amount,
  AnalyticsApplicationUsage,
  AnalyticsRevenue,
  AnalyticsRevenueCurrency,
  AnalyticsWindow,
} from "../admin-client";
import { useAnalyticsApplications, useAnalyticsRevenue } from "../admin-hooks";
import { ErrorAlert, RequireOperator, can } from "./guards";
import { NotPermitted } from "./packages";
import { ASSUMED_EXPONENT, currencyExponent, formatUnits } from "./money-units";
import { MONEY, READ, WindowNotices, WindowPicker } from "./analytics-common";
import { sameWindow } from "./analytics-charts";
import { formatRate, windowMeasured } from "./analytics-window";
import type { AnalyticsSearch } from "./analytics-window";
import {
  arpuSeries,
  moneyMoved,
  paymentsSeries,
  payingUsersSeries,
  rankApplications,
  revenueState,
  rowMoney,
  settledSeries,
  spanDays,
  subscriptionSeries,
  topupOutcomeSeries,
  topupSuccessRate,
  truncated,
} from "./analytics-revenue";

/**
 * `/analytics/revenue` — the screen that is nothing but money.
 *
 * Every other analytics surface answers a question about TRAFFIC and carries
 * money as a column somebody may or may not be allowed to see. This one has no
 * such column, which is why the two grants land differently here:
 *
 *   * `analytics:read` gates the surface, as everywhere else.
 *   * `wallets:read` is a DOOR rather than a filter. The Admin API takes it at
 *     the route — a revenue report with its money withheld is a window object
 *     and a list of currency codes — and answers 403 to an operator holding
 *     only the first grant. So this screen refuses in the same shape the
 *     server does, rather than rendering a page of placeholders.
 *
 * And the rule the whole layout is built around: THERE IS NO TOTAL. The
 * document has one entry per currency and no grand total, no reporting
 * currency and no cross-currency arithmetic anywhere, because adding a euro to
 * a dollar produces a number in no unit and this platform records no exchange
 * rate that would let it. Each currency gets its own run of cards, every card
 * title names the unit, and nothing on the page adds a figure from one section
 * to a figure in another. The one document on the page that IS summed into a
 * single reporting currency — the per-application table, which comes from a
 * different route — says so at the top and marks every row that is not in it.
 */
export function Revenue({ search }: { search: AnalyticsSearch }) {
  return (
    <RequireOperator>
      {(session) => {
        // Checked in this order because it is the order the server checks
        // them in: an operator with neither grant should be told about the
        // one that gates the whole surface, not about the one that gates this
        // corner of it.
        if (!can(session.operator, READ)) {
          return <NotPermitted title="Revenue" permission={READ} />;
        }
        if (!can(session.operator, MONEY)) {
          return <NotPermitted title="Revenue" permission={MONEY} />;
        }
        return <RevenueScreen search={search} />;
      }}
    </RequireOperator>
  );
}

/**
 * An Amount as this screen draws it.
 *
 * Through `money-units.ts` and not through the wire's own `amount` string,
 * which is the same figure: every money reading on this page — a tile, an axis
 * tick, a row of a chart's hidden table — has to come out of ONE formatter, or
 * a bar and the tile above it can disagree about what 1250000000 looks like.
 * The charts can only be given micros, so micros is what the tiles are given
 * too, and this is where all of it converges.
 *
 * The exponent comes from the currency the Amount itself names, never from the
 * section around it: micros per major unit is 10^6 for JPY and 10^8 for USD,
 * so assuming is a hundredfold error on one of the eight currencies the
 * platform transacts in.
 */
function amountUnits(a: Amount): string {
  return `${formatUnits(a.micros, currencyExponent(a.currency) ?? ASSUMED_EXPONENT)} ${
    a.currency || "units"
  }`;
}

function RevenueScreen({ search }: { search: AnalyticsSearch }) {
  const navigate = useNavigate();
  const query = { since: search.since, until: search.until, days: search.days };
  const report = useAnalyticsRevenue(query);
  const data = report.data;

  const state = revenueState(
    data !== undefined,
    report.error !== null && report.error !== undefined,
    data ? windowMeasured(data.available_from, data.window.until) : false,
    data?.currencies.length ?? 0,
  );

  return (
    <>
      <PageHeader
        title="Revenue"
        description="What the platform took in, what its usage settled at, and what was left — one section per currency, never added together. Every figure is a nightly rollup of billing.revenue_daily."
      />

      <Card title="Window">
        <WindowPicker
          search={search}
          busy={report.isFetching}
          apply={(next) => void navigate({ to: "/analytics/revenue", search: next })}
          idPrefix="revenue"
        />
      </Card>

      <Card
        title={
          data
            ? `${data.window.since} to ${data.window.until} (${data.window.days} days)`
            : "The money picture"
        }
      >
        {/* Loading, failed, unmeasured, quiet and ready, in that order and
            never folded into one another. A failed read is not an empty
            window, an unmeasured window is not a quiet one, and a quiet
            window is a real reading about the business. */}
        <ErrorAlert error={report.error} title="The revenue report could not be loaded" />
        {data ? (
          <WindowNotices
            window={data.window}
            // Both undefined on purpose. `WindowNotices`' third banner exists
            // to warn that a total in one currency is hiding the others, and
            // this document has no such total to warn about — it is the
            // per-currency split that banner points readers at.
            currency={undefined}
            currencies={undefined}
            search={search}
          />
        ) : null}
        {data ? <Coverage report={data} /> : null}
        {state.kind === "loading" ? <Spinner label="Loading the revenue report" /> : null}
        {state.kind === "unmeasured" && data ? (
          <EmptyState title="Nothing has been measured over these days">
            <p>
              {data.available_from === null
                ? "No daily revenue rollup has been written yet, so there is nothing to report — not even a zero. The worker writes them nightly; a deployment that has just been brought up has none until its first sweep runs."
                : `The rollups start on ${data.available_from}, which is after this window ends. Ask about a later window to see figures.`}
            </p>
            <p>
              Zeros are not shown here on purpose: &ldquo;the platform earned nothing&rdquo; and
              &ldquo;nobody had measured what it earned&rdquo; are different facts about a business,
              and a row of zeros cannot tell you which one you are looking at.
            </p>
          </EmptyState>
        ) : null}
        {state.kind === "quiet" && data ? (
          <EmptyState title="No money moved in this window">
            <p>
              These days were measured and they carry no revenue rollup in any currency: no top-up,
              no subscription charge, no refund and no settled inference. That is a reading rather
              than a gap — the sweep reached {data.window.since} to {data.window.until} and found
              nothing to record.
            </p>
          </EmptyState>
        ) : null}
        {state.kind === "ready" && data ? <Contents report={data} /> : null}
      </Card>

      {state.kind === "ready" && data
        ? data.currencies.map((entry) => (
            <CurrencySection key={entry.currency} entry={entry} window={data.window} />
          ))
        : null}

      <ApplicationsCard search={search} window={data?.window} />
    </>
  );
}

/**
 * What is below, in words, and the one thing the headings cannot say.
 *
 * A reader who scrolls into the second currency's cards has to know before
 * they start that the first set of figures is not a subset, a conversion or a
 * component of the second. The list is alphabetical by CODE and not by size,
 * because ranking one currency against another by magnitude is the same
 * forbidden comparison as adding them — which is worth stating where the
 * ordering is visible.
 */
function Contents({ report }: { report: AnalyticsRevenue }) {
  const codes = report.currencies.map((entry) => entry.currency);
  return (
    <p className="esm-muted-text">
      {codes.length === 1 ? (
        <>
          Everything below is in <strong>{codes[0]}</strong>. It is the only currency these days
          moved money in.
        </>
      ) : (
        <>
          These days moved money in {codes.length} currencies: <strong>{codes.join(", ")}</strong>.
          Each has its own section below and nothing in one is added to, converted into or compared
          against anything in another — the sections are in alphabetical order by code, because
          ranking currencies by size is the same arithmetic the split exists to refuse.
        </>
      )}
    </p>
  );
}

/**
 * The coverage banner: the answer covers fewer days than the heading above it.
 *
 * The series starts at the first day the rollups reach, so a window asked back
 * past the sweep's first run gets a shorter answer — and the totals in every
 * tile below are the totals of the days that EXIST, not of the span named in
 * the card title. Left unsaid, an operator reads a 90-day heading over a
 * fortnight of figures and concludes the platform is quieter than it is.
 */
function Coverage({ report }: { report: AnalyticsRevenue }) {
  const from = report.available_from;
  if (from === null || !truncated(from, report.window.since, report.window.until)) {
    return null;
  }
  // Both numbers in the sentence come off the same two dates. Counting the
  // days from the series instead would put a figure beside a span it can
  // disagree with, and the reader has no way to tell which of the two is the
  // coverage.
  const days = spanDays(from, report.window.until);
  return (
    <Alert tone="info" title="The answer is shorter than the window">
      The rollups start on {from}, which is inside this window, so every figure below covers{" "}
      {from} to {report.window.until} — {days} {days === 1 ? "day" : "days"} under a heading that
      names {report.window.days}. The days before {from} are not zeros that were measured; they
      were not measured at all.
    </Alert>
  );
}

/* -------------------------------------------------------------------------- *
 * One currency.
 *
 * Five cards, in the order an operator reads the money: what was settled, what
 * came in, whether the payments worked, what the subscriptions did, and who
 * paid. Every card title carries the currency code, because a reader who
 * arrives at one by scrolling has no other way to know which section they are
 * in — and a figure whose unit has to be inferred from its position on a page
 * is a figure that gets pasted into the wrong total.
 * -------------------------------------------------------------------------- */

function CurrencySection({
  entry,
  window: w,
}: {
  entry: AnalyticsRevenueCurrency;
  window: AnalyticsWindow;
}) {
  const code = entry.currency;
  const exponent = currencyExponent(code) ?? ASSUMED_EXPONENT;
  // The one money formatter for every chart in this section. Built from the
  // currency the entry names, so a JPY section's axis is not a hundredfold
  // out — see money-units.ts.
  const format = (micros: number): string => formatUnits(micros, exponent);
  const t = entry.totals;
  const rate = topupSuccessRate(t);
  const attempts = t.topup_count + t.topup_failed_count;

  // Keyed on the entry, so a keystroke in the window picker — which is
  // another component's state — cannot rebuild six series.
  const settled = useMemo(() => settledSeries(entry.series), [entry]);
  const payments = useMemo(() => paymentsSeries(entry.series), [entry]);
  const topups = useMemo(
    () => topupOutcomeSeries(entry.series, CHART_POSITIVE, CHART_NEGATIVE),
    [entry],
  );
  const subscriptions = useMemo(() => subscriptionSeries(entry.series), [entry]);
  const payers = useMemo(() => payingUsersSeries(entry.series), [entry]);
  const arpu = useMemo(() => arpuSeries(entry.series), [entry]);

  return (
    <>
      <Card title={`${code} — headline figures`}>
        <p className="esm-muted-text">
          Every figure in this section is in {code}, over {w.since} to {w.until}. Nothing here is
          added to any other section on the page.
        </p>
        {!moneyMoved(t) ? (
          <p className="esm-muted-text">
            Nothing moved in {code} over these days. The zeros below were measured — this currency
            has a rollup row, and it records that nothing happened.
          </p>
        ) : null}
        <div className="esm-stat-grid">
          <StatTile
            label={`Gross revenue (${code})`}
            value={amountUnits(t.gross_revenue)}
            hint={`Top-ups plus subscription charges. Not the settled inference figures below — those are what this money was spent on, and adding the two would count the same ${code} twice.`}
          />
          <StatTile
            label={`Refunds (${code})`}
            value={amountUnits(t.refunds)}
            hint="A positive magnitude, and already subtracted in net revenue. Adding it to anything on this page is the mistake the shape exists to prevent."
          />
          <StatTile
            label={`Net revenue (${code})`}
            value={amountUnits(t.net_revenue)}
            hint="Gross less refunds. Negative in a window whose refunds settle against an earlier window's payments — a real reading, not an error."
          />
          <StatTile
            label={`Settled inference revenue (${code})`}
            value={amountUnits(t.inference_sell)}
            hint="What the usage in this window settled at, read through the settlements. Not the same as the billed figure on the analytics dashboard, which includes usage whose settlement is still open."
          />
          <StatTile
            label={`Provider cost (${code})`}
            value={amountUnits(t.inference_cost)}
            hint="What the same usage cost upstream."
          />
          <StatTile
            label={`Margin (${code})`}
            value={amountUnits(t.margin)}
            hint="Settled revenue minus provider cost. Negative is a real reading and the one this screen exists to surface."
          />
          <StatTile
            label="Paying users"
            // "up to", because the wire field is named upper bound and says
            // why: the rollup stores a per-(day, application, currency)
            // distinct count and never the ids.
            value={`up to ${t.paying_users_upper_bound.toLocaleString()}`}
            hint="An upper bound in two directions: somebody who paid on ten days is counted ten times, and somebody who paid in two applications on one day is counted twice."
          />
          <StatTile
            label={`Revenue per paying user (${code})`}
            // A null ARPU is not a zero and must never be drawn as one: "the
            // average customer paid nothing" is a claim about a customer base
            // that paid. The tile renders the words in place of the figure,
            // and the hint repeats them where a sighted reader will see them
            // without hovering.
            value={t.arpu === null ? null : amountUnits(t.arpu)}
            unavailable="not applicable (no active users)"
            hint={
              t.arpu === null
                ? "Not applicable (no active users) — nobody paid in this window, so there is no average to take. That is not the same as an average of zero."
                : `Net revenue over the paying-user count beside it. The denominator over-counts, so this figure is closer to zero than the true one.`
            }
          />
        </div>
      </Card>

      <Card title={`Settled revenue, cost and margin per day (${code})`}>
        <p className="esm-muted-text">
          Side by side rather than stacked: margin is revenue minus cost rather than a part of it,
          and on a written-off day it is negative — which a stacked bar cannot draw at all. A bar
          below the line is a real reading.
        </p>
        <GroupedBarChart
          name={`Settled revenue, cost and margin per day, in ${code}`}
          labels={settled.labels}
          series={settled.series}
          format={format}
          // Money ticks, not counts: "1,250,000.00" needs a column half as
          // wide again as the default, and an end-anchored label longer than
          // its column runs off the edge of the drawing.
          valueAxis={128}
          pointHeader="Day"
        />
      </Card>

      <Card title={`Money in, and how the top-ups fared (${code})`}>
        <p className="esm-muted-text">
          The bars are GROSS: top-ups and subscription charges, which are what gross revenue is made
          of. Refunds are not a third segment — they are money going the other way, and a segment
          for them would grow the bar with the thing that shrinks the total. The refund figure is in
          the tiles above.
        </p>
        <StackedBarChart
          name={`Money in by route per day, in ${code}`}
          labels={payments.labels}
          series={payments.series}
          format={format}
          pointHeader="Day"
        />
        <div className="esm-stat-grid">
          <StatTile
            label={`Top-ups (${code})`}
            value={amountUnits(t.topup_revenue)}
            hint={`${t.topup_count.toLocaleString()} settled ${
              t.topup_count === 1 ? "payment" : "payments"
            }.`}
          />
          <StatTile
            label="Top-up success rate"
            // Null over no attempts, never 0%: "nobody tried to top up" and
            // "every attempt was declined" are the same division without the
            // guard, and the second is a payment provider on fire.
            value={rate === null ? null : formatRate(rate)}
            unavailable="No top-up was attempted in this window, so there is no rate to take."
            hint={
              rate === null
                ? "No top-up was attempted in this window. That is not a rate of zero, which would say every attempt was declined."
                : `${t.topup_count.toLocaleString()} of ${attempts.toLocaleString()} attempts settled; ${t.topup_failed_count.toLocaleString()} failed. A failed payment moved no money, so it has a count here and no amount anywhere.`
            }
          />
          <StatTile
            label="Failed top-ups"
            value={t.topup_failed_count.toLocaleString()}
            hint="Counted and deliberately never given an amount: a failed payment moved no money, and an amount here would eventually be added to a revenue total."
          />
        </div>
        <p className="esm-muted-text">
          Attempts per day, split by outcome — the settled ones share the baseline so the shape of
          the traffic is readable, and the failures cap each bar.
        </p>
        <StackedBarChart
          name="Top-up attempts by outcome per day"
          labels={topups.labels}
          series={topups.series}
          format={(value) => value.toLocaleString()}
          pointHeader="Day"
        />
      </Card>

      <Card title={`Subscriptions (${code})`}>
        <p className="esm-muted-text">
          The three figures are deliberately unrelated to each other: a trial that started, a
          cancellation before the first renewal and a silent renewal are all ordinary days, and none
          of the three is derivable from the other two.
        </p>
        <div className="esm-stat-grid">
          <StatTile
            label={`Subscription revenue (${code})`}
            value={amountUnits(t.subscription_revenue)}
            hint={`${t.subscription_charge_count.toLocaleString()} ${
              t.subscription_charge_count === 1 ? "charge" : "charges"
            } settled in this window.`}
          />
          <StatTile
            label="Started"
            value={t.new_subscriptions.toLocaleString()}
            hint="Subscriptions that began in the window, whether or not they have been charged yet."
          />
          <StatTile
            label="Cancelled"
            value={t.cancelled_subscriptions.toLocaleString()}
            hint="Subscriptions cancelled in the window, whether or not they were ever charged."
          />
        </div>
        <GroupedBarChart
          name="Subscriptions started and cancelled per day"
          labels={subscriptions.labels}
          series={subscriptions.series}
          format={(value) => value.toLocaleString()}
          pointHeader="Day"
        />
      </Card>

      <Card title={`Paying users and revenue per user (${code})`}>
        <p className="esm-muted-text">
          Shape only — the window's totals are in the tiles above. The count is an upper bound: the
          rollup stores a per-(day, application, currency) distinct count and never the ids, so a
          person who paid in two applications on one day is in it twice.
        </p>
        <Sparkline
          name="Paying users per day (upper bound)"
          points={payers}
          format={(value) => value.toLocaleString()}
          width={720}
          height={120}
          pointHeader="Day"
          valueHeader="Paying users (up to)"
        />
        <p className="esm-muted-text">
          Net revenue over that count, per day. A day nobody paid has NO average and the line breaks
          there rather than touching the baseline — a zero would say the average customer paid
          nothing on a day there was no average to take. The breaks are counted in the chart&rsquo;s
          own summary.
        </p>
        <Sparkline
          name={`Revenue per paying user per day, in ${code}`}
          points={arpu}
          format={format}
          width={720}
          height={120}
          pointHeader="Day"
          valueHeader={`Revenue per paying user (${code})`}
        />
      </Card>
    </>
  );
}

/* -------------------------------------------------------------------------- *
 * Which product earns.
 *
 * A SECOND read, over GET /admin/v1/analytics/applications, and it has to be:
 * the revenue route reads billing.revenue_daily and nothing else, so it cannot
 * name an application without reading a catalogue it is not allowed to read.
 *
 * The consequence is the thing this card spends its words on. That document
 * sums its money columns into ONE reporting currency, which is exactly what
 * every section above refuses to do — so an application billing in another
 * currency appears with zero money, and the row's own `currency` field is the
 * only thing that tells that from "earned nothing".
 * -------------------------------------------------------------------------- */

function ApplicationsCard({
  search,
  window: w,
}: {
  search: AnalyticsSearch;
  window: AnalyticsWindow | undefined;
}) {
  const query = useAnalyticsApplications({
    since: search.since,
    until: search.until,
    days: search.days,
  });
  const answer = query.data;
  const code = answer?.currency;
  const exponent = currencyExponent(code) ?? ASSUMED_EXPONENT;
  const measured = answer ? windowMeasured(answer.available_from, answer.window.until) : false;
  // Complete before it is ranked: this route has no `limit` and no `sort`, and
  // it returns one row per application in the CATALOGUE — including the ones
  // that earned nothing. So re-ordering it here names the same set of products
  // in a different order, which is not true of the top-users table.
  const rows = useMemo(
    () => (answer ? rankApplications(answer.applications, code) : []),
    [answer, code],
  );
  // Two routes, two windows. Both clamp at 90 days and both end on the last
  // complete UTC day, so they agree in practice — and it is checked rather
  // than assumed, because a card that borrowed the heading above it would say
  // so silently on the day that changes.
  const aligned = w === undefined || answer === undefined || sameWindow(w, answer.window);

  const columns: Array<Column<AnalyticsApplicationUsage>> = [
    {
      key: "application",
      header: "Application",
      render: (row) => (
        <>
          <Link to="/applications/$applicationId" params={{ applicationId: row.application.id }}>
            {row.application.name}
          </Link>
          <div className="esm-muted-text">
            {row.application.slug}
            {/* Not a reason to hide the row: it says how to READ the figures
                beside it. A disabled application's collapsing revenue is a
                measurement of the disabling. */}
            {row.status === "active" ? null : ` — ${row.status}`}
          </div>
        </>
      ),
    },
    {
      key: "currency",
      header: "Settles in",
      render: (row) => <RowCurrency row={row} reportCurrency={code} />,
    },
    {
      key: "payments",
      header: `Payments in (${code ?? "units"})`,
      align: "end",
      render: (row) => (
        <RowMoneyCell row={row} reportCurrency={code} exponent={exponent} read={(r) => r.net_payments_micros}>
          {(revenue) => (
            <div className="esm-muted-text">
              {formatUnits(revenue.topup_micros, exponent)} topped up,{" "}
              {formatUnits(revenue.subscription_charge_micros, exponent)} subscribed, less{" "}
              {formatUnits(revenue.refund_micros, exponent)} refunded
            </div>
          )}
        </RowMoneyCell>
      ),
    },
    {
      key: "settled",
      header: `Settled revenue (${code ?? "units"})`,
      align: "end",
      render: (row) => (
        <RowMoneyCell row={row} reportCurrency={code} exponent={exponent} read={(r) => r.settled_sell_micros}>
          {(revenue) => (
            <div className="esm-muted-text">
              {formatUnits(revenue.settled_cost_micros, exponent)} cost
            </div>
          )}
        </RowMoneyCell>
      ),
    },
    {
      key: "margin",
      header: `Margin (${code ?? "units"})`,
      align: "end",
      render: (row) => (
        <RowMoneyCell
          row={row}
          reportCurrency={code}
          exponent={exponent}
          read={(r) => r.settled_margin_micros}
        />
      ),
    },
    {
      key: "payers",
      header: "Paying users",
      align: "end",
      render: (row) => {
        const state = rowMoney(row, code);
        if (state === "withheld" || row.revenue === undefined) {
          return <span className="esm-muted-text">unknown</span>;
        }
        // The count is summed inside the same reporting-currency filter the
        // money columns are, so a foreign row's is zero for the same reason
        // its revenue is — a fact about the summation, not about who paid.
        if (state === "foreign") {
          return <span className="esm-muted-text">settles in {row.currency}</span>;
        }
        // An upper bound, like every other head count on this surface.
        return <>up to {row.revenue.paying_users_upper_bound.toLocaleString()}</>;
      },
    },
  ];

  return (
    <Card title="Which product earns">
      <p className="esm-muted-text">
        One row per application in the catalogue, including the ones that took nothing — a product
        that earned nothing writes no rollup row, and a list built from the rollup would show
        exactly the products that are working. Ranked by settled margin, biggest first.
      </p>

      <ErrorAlert error={query.error} title="The per-application figures could not be loaded" />

      {/* Only with rows to be confused by. On an empty table the warning
          describes a summation nothing was summed into, which reads as a
          caveat about figures that are not on the page. */}
      {answer && rows.length > 0 ? (
        <Alert tone="warning" title={`These columns are summed in ${answer.currency || "one currency"}`}>
          Unlike the sections above, this report picks ONE reporting currency and adds the
          applications up in it. An application that settles in another currency therefore shows
          zero money here, and its own unit is in the &ldquo;Settles in&rdquo; column — that column
          is the only thing on the row that tells a foreign figure from an application that earned
          nothing.
          {answer.currencies && answer.currencies.length > 1
            ? ` These days carry ${answer.currencies.join(", ")}.`
            : null}
        </Alert>
      ) : null}
      {!aligned && answer ? (
        <Alert tone="info" title="These are different days">
          This report covers {answer.window.since} to {answer.window.until}, which is not the window
          the sections above are answering.
        </Alert>
      ) : null}

      {query.isPending && !query.error ? (
        <Spinner label="Loading the per-application figures" />
      ) : null}
      {answer ? (
        <DataTable<AnalyticsApplicationUsage>
          rows={rows}
          rowKey={(row) => row.application.id}
          empty={
            measured
              ? "No application is in the catalogue for this window."
              : "Nothing has been measured over these days, so there is nothing to rank — which is not the same as no product having earned anything."
          }
          columns={columns}
        />
      ) : null}
      {/* Only with rows on the page. With none, the table's own empty text
          has already said it, and saying it twice reads as two facts. */}
      {answer && rows.length > 0 && !measured ? (
        <p className="esm-muted-text">
          The usage rollups do not reach this window
          {answer.available_from === null
            ? " at all yet"
            : `; they start on ${answer.available_from}`}
          , so every figure in the table is an unmeasured zero rather than a measured one.
        </p>
      ) : null}
    </Card>
  );
}

/** The row's own unit, which is the only thing on the row that tells a foreign
 *  figure from an application that earned nothing. */
function RowCurrency({
  row,
  reportCurrency,
}: {
  row: AnalyticsApplicationUsage;
  reportCurrency: string | undefined;
}) {
  if (row.currency === undefined) {
    return <span className="esm-muted-text">unknown</span>;
  }
  if (row.currency === "") {
    // The wire's own value for an application that settled nothing at all.
    return <span className="esm-muted-text">nothing settled</span>;
  }
  return (
    <>
      <div>{row.currency}</div>
      {rowMoney(row, reportCurrency) === "foreign" ? (
        <div className="esm-muted-text">not {reportCurrency}</div>
      ) : null}
    </>
  );
}

/**
 * One money cell, in the three cases `rowMoney` keeps apart.
 *
 * A foreign row prints the words and NOT the zero the reporting currency's
 * column holds for it. The zero is arithmetically true — the report summed
 * this application's dollars and found none — and printing it would answer a
 * question about the business with a fact about the summation.
 */
function RowMoneyCell({
  row,
  reportCurrency,
  exponent,
  read,
  children,
}: {
  row: AnalyticsApplicationUsage;
  reportCurrency: string | undefined;
  exponent: number;
  read: (revenue: NonNullable<AnalyticsApplicationUsage["revenue"]>) => number;
  children?: (revenue: NonNullable<AnalyticsApplicationUsage["revenue"]>) => ReactNode;
}) {
  const state = rowMoney(row, reportCurrency);
  if (state === "withheld" || row.revenue === undefined) {
    return <span className="esm-muted-text">unknown</span>;
  }
  if (state === "foreign") {
    return <span className="esm-muted-text">settles in {row.currency}</span>;
  }
  return (
    <>
      <span className="esm-money">{formatUnits(read(row.revenue), exponent)}</span>
      {children ? children(row.revenue) : null}
    </>
  );
}

/** Re-exported so the route file has one import for the screen and its search
 *  shape, exactly as `analytics.tsx` does. */
export type { AnalyticsSearch };
